import React, { useEffect } from 'react';
import { useTitle } from '../hooks/useTitle';
import { LanguageSelector } from '../components/LanguageSelector';
import { AdminBar } from '../components/AdminBar';
import { ArrowRight, ClipboardCheck, Clock, Shield, Workflow, CheckCircle2, MessageCircle, FileCheck, Zap, Settings, Users } from 'lucide-react';

export function AuditAutomation() {
  const { setTitle } = useTitle();
  
  useEffect(() => {
    setTitle('Automatisation des Rapports d\'Audit - Novster AI');
  }, [setTitle]);

  return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 py-20">
          <h1 className="text-4xl md:text-6xl font-bold text-center text-gray-900 mb-6 animate-slideUp">
            Automatisez la rédaction de vos{' '}
            <span className="text-blue-600">rapports techniques</span>
          </h1>
          <p className="text-xl text-center text-gray-600 max-w-3xl mx-auto mb-12 animate-slideUp">
            Libérez vos équipes des tâches répétitives et concentrez-vous sur votre expertise technique. 
            Notre solution d'automatisation par IA transforme vos processus de rédaction de rapports.
          </p>
          <div className="flex justify-center mb-12">
            <a
              href="https://tidycal.com/quentin.tousart/novster-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold flex items-center gap-2 transform hover:scale-105 transition-all animate-slideUp animate-float shadow-lg"
            >
              Réserver ma consultation gratuite
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
          <div className="flex justify-center">
            <img
              src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200"
              alt="Audit automation"
              className="rounded-xl shadow-2xl max-w-4xl w-full animate-slideUp"
              style={{ '--delay': '0.4s' } as React.CSSProperties}
            />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Notre solution répond à vos enjeux quotidiens
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: "Gardez le contrôle total",
                description: "Validez chaque étape du processus. Notre solution vous permet de superviser et d'ajuster les rapports générés selon vos besoins."
              },
              {
                icon: Clock,
                title: "Gagnez un temps précieux",
                description: "Automatisez les tâches répétitives de rédaction et permettez à vos experts de se concentrer sur leur véritable valeur ajoutée."
              },
              {
                icon: ClipboardCheck,
                title: "Garantissez la conformité",
                description: "Générés automatiquement, vos rapports respectent systématiquement les normes et standards de votre secteur."
              },
              {
                icon: Workflow,
                title: "Simplifiez votre workflow",
                description: "Notre solution s'intègre naturellement à vos outils existants. Une transition en douceur, accompagnée par nos experts."
              }
            ].map((feature, index) => (
              <div
                key={feature.title}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all animate-slideUp"
                style={{ '--delay': `${0.2 * index}s` } as React.CSSProperties}
              >
                <feature.icon className="w-12 h-12 text-blue-600 mb-6" />
                <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Les avantages de notre solution
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="space-y-6">
                {[
                  {
                    icon: Zap,
                    title: "Déploiement rapide",
                    description: "Opérationnel en quelques jours"
                  },
                  {
                    icon: CheckCircle2,
                    title: "Validation point par point",
                    description: "Gardez la main sur chaque aspect"
                  },
                  {
                    icon: FileCheck,
                    title: "Personnalisation",
                    description: "Adaptée à vos modèles de rapports"
                  },
                  {
                    icon: Users,
                    title: "Support dédié",
                    description: "Une équipe à votre écoute"
                  },
                  {
                    icon: Settings,
                    title: "Mises à jour régulières",
                    description: "Pour rester à la pointe"
                  }
                ].map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-4">
                    <benefit.icon className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold mb-1">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800"
                alt="Platform interface"
                className="rounded-xl shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Ce que nos clients disent
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
              <MessageCircle className="w-12 h-12 text-blue-600 mb-6" />
              <blockquote className="text-xl text-gray-700 mb-6">
                "La transition s'est faite en douceur, avec la possibilité de valider chaque étape. 
                Nous avons pu augmenter notre capacité de traitement de 40% tout en gardant le contrôle 
                total sur la qualité de nos rapports."
              </blockquote>
              <div className="flex items-center gap-4">
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100"
                  alt="Thomas D."
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold">Thomas D.</div>
                  <div className="text-gray-600">Directeur Technique, Bureau de contrôle environnemental</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">
            Découvrez comment optimiser vos processus
          </h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto">
            Consultation gratuite et personnalisée avec l'un de nos experts. 
            Nous analyserons ensemble vos besoins spécifiques et vous présenterons 
            les solutions adaptées.
          </p>
          <a
            href="https://tidycal.com/quentin.tousart/novster-ai"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-gray-100 transform hover:scale-105 transition-all"
          >
            Réserver ma consultation gratuite
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}